import React, { useEffect, useState } from 'react';
import { PLAYER } from '../playerData';
import { MIN_PLAYERS, MAX_PLAYERS } from './config';
import { BOT_NAMES } from './botNames';
import { ASSETS } from '../assetManifest';

function Lobby({ gameInterface, onFinishGame }) {
  const [usersList, setUsersList] = useState([]);
  const [lobbyId, setLobbyId] = useState(gameInterface?.lobbyId);
  const [inputLobbyId, setInputLobbyId] = useState('');
  const [joinError, setJoinError] = useState('');
  const [isJoining, setIsJoining] = useState(false);

  useEffect(() => {
    const updateUsers = (data) => {
      setUsersList(Object.values(gameInterface.users));
      setLobbyId(data.lobby_id);
    };

    const handleJoinLobbyFailed = (error) => {
      setJoinError(error);
      setIsJoining(false);
    };

    const handleConnected = () => {
      console.log('Lobby: Connected to server, attempting to join lobby');
      if (!gameInterface.lobbyId && !isJoining) {
        setIsJoining(true);
        gameInterface.joinLobby();
      }
    };

    gameInterface.on('player_list_changed', updateUsers);
    gameInterface.on('join_lobby_failed', handleJoinLobbyFailed);
    gameInterface.on('connected', handleConnected);

    if (gameInterface.isConnected) {
      handleConnected();
    }

    return () => {
      if (gameInterface.callbacks.player_list_changed === updateUsers) {
        gameInterface.callbacks.player_list_changed = null;
      }
      if (gameInterface.callbacks.join_lobby_failed === handleJoinLobbyFailed) {
        gameInterface.callbacks.join_lobby_failed = null;
      }
      if (gameInterface.callbacks.connected === handleConnected) {
        gameInterface.callbacks.connected = null;
      }
    };
  }, [gameInterface, isJoining]);

  const handleLobbyIdChange = (e) => {
    setInputLobbyId(e.target.value.toUpperCase());
  };

  const handleJoinLobby = () => {
    setJoinError('');
    gameInterface.joinLobby(inputLobbyId.trim() || null);
  };

  const addBots = () => {
    const currentPlayers = Object.values(gameInterface.users).length;
    const botsNeeded = MAX_PLAYERS - currentPlayers;
    
    for (let i = 0; i < botsNeeded; i++) {
      const botId = `bot-${i}`;
      
      gameInterface.users[botId] = {
        sid: botId,
        name: `Bot ${i + 1}`,
        stats: { ...PLAYER.stats },
        is_bot: true,
        lobby_id: lobbyId
      };
    }
  };

  const handleStartGame = () => {
    if (usersList.length < MAX_PLAYERS) {
      addBots();
    }
    gameInterface.sendStartGame();
  };

  return (
    <div className="h-full w-full relative overflow-hidden">
      <img 
        className="w-full h-full absolute left-0 top-0" 
        src={ASSETS.backgrounds.lobby_bg.path} 
        alt="Lobby Background" 
      />
      
      <div className="w-[346px] h-[780px] absolute left-[22px] top-[25px]">
        <div className="w-[346px] h-[49px] text-center text-white text-[28px] font-black font-['Asap'] leading-[50px] [text-shadow:_0px_1px_1px_rgb(0_0_0_/_1.00)]">
          Join or Create Race
        </div>
        
        {lobbyId && (
          <div className="w-[273px] h-12 absolute left-[44px] top-[49px]">
            <div className="w-[169px] h-12 absolute left-0 top-0 text-right text-[#29a69d] text-xl font-black font-['Asap'] leading-[45px] [text-shadow:_0px_1px_1px_rgb(0_0_0_/_1.00)]">
              Your Lobby ID :
            </div>
            <div className="w-[95px] h-12 absolute left-[178px] top-0 text-[#928aff] text-xl font-black font-['Asap'] leading-[45px] [text-shadow:_0px_1px_1px_rgb(0_0_0_/_1.00)]">
              {lobbyId}
            </div>
          </div>
        )}
        
        <div className="w-[346px] h-[191px] absolute left-0 top-[103px]">
          <div className="w-[346px] h-[191px] absolute left-0 top-0 bg-white/80 rounded-lg border-2 border-black"></div>
          <div className="w-[316px] h-[55px] absolute left-[15px] top-[6px] text-center text-[#040404] text-lg font-black font-['Asap'] leading-[50px]">
            Enter a race id to join a friend's lobby
          </div>
          <input
            className="w-[316px] h-[50px] absolute left-[15px] top-[61px] bg-[#f9f1ff]/75 rounded-lg border-2 border-black px-4 text-black"
            type="text"
            value={inputLobbyId}
            onChange={handleLobbyIdChange}
            placeholder="Enter Lobby ID"
          />
          <div className="w-[114px] h-[43px] absolute left-[116px] top-[131px]">
            <button
              className="w-[114px] h-[43px] absolute left-0 top-0 bg-[#83ffd9] rounded-md shadow-[inset_0px_-6px_2px_0px_rgba(65,138,116,1.00)] border-2 border-black"
              onClick={handleJoinLobby}
            >
              <div className="w-[114px] h-[39.42px] text-center text-[#141313] text-base font-black font-['Asap'] capitalize leading-[40px]">
                Join Lobby
              </div>
            </button>
          </div>
          {joinError && (
            <p className="absolute left-[15px] top-[180px] text-red-500 text-center w-[316px]">
              {joinError}
            </p>
          )}
        </div>
        
        {lobbyId && (
          <>
            <div className="w-[346px] h-[327px] absolute left-0 top-[313px]">
              <div className="w-[346px] h-[327px] absolute left-0 top-0 bg-white/80 rounded-lg border-2 border-black"></div>
              <div className="w-[316px] h-11 absolute left-[15px] top-[11px] text-center text-[#434caf] text-xl font-black font-['Asap'] leading-[45px] [text-shadow:_0px_1px_1px_rgb(0_0_0_/_1.00)]">
                Connected Players
              </div>
              <div className="w-[316px] h-64 absolute left-[15px] top-[55px]">
                {usersList.map((user, index) => (
                  <div 
                    key={user.sid}
                    className="w-[316px] h-8 absolute left-0 text-center text-[#f53de0]/95 text-xl font-extrabold font-['Asap'] [text-shadow:_0px_1px_1px_rgb(0_0_0_/_1.00)]"
                    style={{ top: `${index * 32}px` }}
                  >
                    {user.name}
                    {user.is_host && ' (Host)'}
                  </div>
                ))}
              </div>
            </div>
            
            {gameInterface.userId === gameInterface.hostId && (
              <div className="w-[129px] h-[49px] absolute left-[102px] top-[660px]">
                <button
                  className={`w-[129px] h-[49px] absolute left-0 top-0 rounded-[5px] border-2 border-black ${
                    usersList.length >= MIN_PLAYERS
                      ? 'bg-[#83ffd9] shadow-[inset_0px_-6px_2px_0px_rgba(84,164,140,1.00)]'
                      : 'bg-gray-400 opacity-70'
                  }`}
                  onClick={handleStartGame}
                  disabled={usersList.length < MIN_PLAYERS}
                >
                  <div className="w-[129px] h-[44.92px] text-center text-[#141313] text-[22px] font-extrabold font-['Asap'] capitalize leading-[45px]">
                    Start
                  </div>
                </button>
                {usersList.length < MIN_PLAYERS && (
                  <p className="absolute w-[200px] left-[-35px] top-[55px] text-center text-yellow-400 text-sm font-bold">
                    Need at least {MIN_PLAYERS} players to start
                  </p>
                )}
              </div>
            )}
            
            <div className="w-[206px] h-[46px] absolute left-[134px] top-[734px] text-center text-white text-2xl font-black font-['Asap'] leading-[45px] [text-shadow:_0px_2px_2px_rgb(0_0_0_/_1.00)]">
              Players : {usersList.length}/{MAX_PLAYERS}
            </div>
          </>
        )}
        
        <div className="w-24 h-9 absolute left-0 top-[739px]">
          <button
            className="w-24 h-9 absolute left-0 top-0 bg-[#ff495e] rounded-[5px] shadow-[inset_0px_-6px_2px_0px_rgba(189,17,20,1.00)] border-2 border-black"
            onClick={onFinishGame}
          >
            <div className="w-24 h-[33px] text-center text-white text-[22px] font-black font-['Asap'] capitalize leading-[30px]">
              Leave
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

export default Lobby;
